import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";

const THRESHOLD = 70;

export const PullToRefresh = () => {
  const queryClient = useQueryClient();
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const active = useRef(false);

  useEffect(() => {
    const onStart = (e: TouchEvent) => {
      if (window.scrollY <= 0) {
        startY.current = e.touches[0].clientY;
        active.current = true;
      } else {
        active.current = false;
      }
    };
    const onMove = (e: TouchEvent) => {
      if (!active.current || startY.current == null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && window.scrollY <= 0) setPull(Math.min(dy * 0.5, 110));
      else setPull(0);
    };
    const onEnd = () => {
      if (!active.current) return;
      active.current = false;
      startY.current = null;
      setPull((p) => {
        if (p >= THRESHOLD) {
          setRefreshing(true);
          queryClient.invalidateQueries();
          setTimeout(() => {
            setRefreshing(false);
            setPull(0);
          }, 900);
          return THRESHOLD;
        }
        return 0;
      });
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [queryClient]);

  if (pull <= 0 && !refreshing) return null;
  return (
    <div data-testid="pull-to-refresh-indicator"
      className="fixed top-0 left-0 right-0 z-[60] flex justify-center pointer-events-none"
      style={{
        transform: `translateY(${Math.min(pull, 90) - 8}px)`,
        opacity: Math.min(pull / THRESHOLD, 1),
        transition: refreshing || pull === 0 ? "transform 0.25s, opacity 0.25s" : "none",
      }}>
      <div className="h-10 w-10 rounded-full bg-[#121212] border border-[#7C3AED]/60 shadow-lg shadow-black/50 flex items-center justify-center">
        <RefreshCw className={`h-4 w-4 ${pull >= THRESHOLD || refreshing ? "text-[#00E5FF]" : "text-gray-400"} ${refreshing ? "animate-spin" : ""}`}
          style={refreshing ? undefined : { transform: `rotate(${pull * 2.5}deg)` }} />
      </div>
    </div>
  );
};
